import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ROLE_LABELS, LOGO_ROUND, FAVICON } from "@/lib/constants";
import { formatDateTime } from "@/lib/date";
import { Home, CalendarDays, ListChecks, Users as UsersIcon, Loader2, AlertTriangle, Megaphone, MessageCircle, KeyRound, Flame, Shirt, ChevronRight, Package } from "lucide-react";
import { SuppliesDialog, supplyKey } from "@/components/SuppliesDialog";

import { tx, lz } from "@/i18n";
import { NoticeText } from "@/components/NoticeText";
const TEAM = ["admin", "host", "cleaner", "subcontractor"];
const GUEST_TYPES = ["checkin", "cleaning", "checkout"];

// Agenda-aandachtspunten: dezelfde rode-alert logica als in de agenda.
const taskComplete = (t) => !!t && (t.status === "done" || (Array.isArray(t.subtasks) && t.subtasks.length > 0 && t.subtasks.every((s) => s.done)));
const linenOverdue = (b) => { if (b.type !== "checkout" || b.linen_done !== false || !b.start) return false; const t = new Date(b.start); t.setHours(22, 0, 0, 0); return Date.now() >= t.getTime(); };
const checklistOverdue = (b, ct, done) => !!ct && !done && !!b.start && (Date.now() - new Date(b.start).getTime() >= 24 * 3600 * 1000);
const sesRed = (b) => b.type === "checkin" && !!b.register_ses && (b.ses_status || 0) === 0;

// Workflow-regels: label + kleur per afspraaktype, afgestemd op de agenda-kleurcodering.
const TYPE_META = lz({
  checkin: { label: "Incheck", bar: "border-l-emerald-400", bg: "bg-emerald-50", text: "text-emerald-700" },
  checkout: { label: "Uitcheck", bar: "border-l-amber-400", bg: "bg-amber-50", text: "text-amber-700" },
  cleaning: { label: "Schoonmaak", bar: "border-l-sky-400", bg: "bg-sky-50", text: "text-sky-700" },
  maintenance: { label: "Onderhoud", bar: "border-l-violet-400", bg: "bg-violet-50", text: "text-violet-700" },
  inspection: { label: "Inspectie", bar: "border-l-orange-400", bg: "bg-orange-50", text: "text-orange-700" },
});

const CARDS = lz([
  { key: "houses", label: "Woningen", icon: Home, color: "text-primary bg-primary/10", to: () => "/app/houses" },
  { key: "upcoming_bookings", label: "Komende afspraken", icon: CalendarDays, color: "text-secondary bg-secondary/10", to: (role) => TEAM.includes(role) ? "/app/calendar" : "/app/reservations" },
  { key: "open_tasks", label: "Open taken", icon: ListChecks, color: "text-blue-600 bg-blue-100", to: () => "/app/tasks", visible: (u) => TEAM.includes(u.role) && u.role !== "host" },
  { key: "supplies", label: "Benodigdheden", icon: Package, color: "text-amber-700 bg-amber-100", visible: (u) => ["admin", "host", "client"].includes(u.role) },
  { key: "chat_unread", label: "Live chat", icon: MessageCircle, color: "text-emerald-600 bg-emerald-100", to: () => "/app/chat", visible: (u) => ["admin", "host", "client", "cleaner", "subcontractor"].includes(u.role) },
  { key: "key_sets", label: "Sleutelbeheer", icon: KeyRound, color: "text-purple-600 bg-purple-100", to: () => "/app/keys", visible: (u) => u.role === "admin" || u.key_access },
  { key: "total_users", label: "Gebruikers", icon: UsersIcon, color: "text-purple-600 bg-purple-100", to: () => "/app/users", visible: (u) => u.role === "admin" },
]);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [notices, setNotices] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [supplies, setSupplies] = useState([]);
  const [suppliesOpen, setSuppliesOpen] = useState(false);
  const isTeam = TEAM.includes(user.role);
  const showNotices = isTeam || user.role === "client";

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => setStats({}));
    if (isTeam) {
      api.get("/bookings").then(({ data }) => {
        setAllBookings(data || []);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const next = (data || [])
          .filter((b) => TYPE_META[b.type] && b.start && new Date(b.start) >= today)
          .sort((a, b) => new Date(a.start) - new Date(b.start))
          .slice(0, 4);
        setUpcoming(next);
      }).catch(() => setUpcoming([]));
      api.get("/tasks").then(({ data }) => setAllTasks(data || [])).catch(() => setAllTasks([]));
    }
    if (["admin", "host", "client"].includes(user.role)) {
      api.get("/supplies/open").then(({ data }) => setSupplies(data || [])).catch(() => setSupplies([]));
    }
    if (user.role === "client") {
      api.get("/reservations").then(({ data }) => setPendingCount((data || []).filter((r) => r.status === "pending").length)).catch(() => setPendingCount(0));
    }
    if (showNotices) {
      api.get("/notices/active").then(({ data }) => {
        const flat = Object.values(data || {}).flat();
        flat.sort((a, b) => (a.priority === "urgent" ? 0 : 1) - (b.priority === "urgent" ? 0 : 1));
        setNotices(flat);
      }).catch(() => setNotices([]));
    }
  }, [showNotices]);

  const cards = CARDS.filter((c) => !c.visible || c.visible(user));
  const isHost = user.role === "host";
  const isAdmin = user.role === "admin";
  const isOwner = user.role === "client";

  const taskFor = (rid, cat) => allTasks.find((t) => t.reservation_id === rid && t.category === cat);
  const agendaAlerts = allBookings.filter((b) => {
    if (b.status === "done") return false;
    const ct = (b.reservation_id && GUEST_TYPES.includes(b.type)) ? taskFor(b.reservation_id, b.type) : null;
    const cdone = taskComplete(ct);
    const hoursNeeded = !!ct && cdone && (ct.worked_hours == null || ct.worked_hours === "");
    return linenOverdue(b) || checklistOverdue(b, ct, cdone) || hoursNeeded || (isAdmin && sesRed(b));
  }).length;

  // Host: vaste tegelvolgorde (Agenda, Live chat, Woningen, Sleutelbeheer) + hernoem afspraken-tegel naar "Agenda".
  const hostOrder = { upcoming_bookings: 0, chat_unread: 1, supplies: 2, key_sets: 3 };
  const displayCards = isHost
    ? cards
        .filter((c) => c.key in hostOrder)
        .sort((a, b) => hostOrder[a.key] - hostOrder[b.key])
        .map((c) => (c.key === "upcoming_bookings" ? { ...c, label: tx("Agenda") } : c))
    : isOwner
    ? cards.map((c) => (c.key === "upcoming_bookings" ? { ...c, label: tx("Komende verblijven") }
        : c.key === "houses" && stats?.houses === 1 ? { ...c, label: tx("Woning") } : c))
    : cards;
  const ownerRed = (key) => isOwner && ((key === "houses" && notices.some((n) => n.priority === "urgent")) || (key === "upcoming_bookings" && pendingCount > 0));

  return (
    <div className="animate-fade-up">
      <div className="relative mb-6 sm:mb-8 overflow-hidden rounded-2xl sm:rounded-3xl border border-border bg-gradient-to-br from-primary/15 via-background to-background p-4 sm:p-10">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative flex flex-row items-center gap-4 sm:gap-6 text-left">
          <img src={FAVICON} alt={tx("Costa Key")} className="sm:hidden h-14 w-14 rounded-full shadow-md shadow-primary/20 shrink-0" data-testid="dashboard-icon-mobile" />
          <img
            src={LOGO_ROUND}
            alt={tx("Costa Key")}
            className="hidden sm:block h-36 w-36 rounded-full shadow-xl shadow-primary/25 shrink-0"
            data-testid="dashboard-logo"
          />
          <div>
            <h1 className="font-display font-bold text-2xl sm:text-4xl tracking-tight">{tx("Hallo")} {user.name?.split(" ")[0]} 👋</h1>
            <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">{tx("Welkom op uw Costa Key portaal ·")} {ROLE_LABELS[user.role]}</p>
          </div>
        </div>
      </div>

      {isTeam && upcoming.length > 0 && (
        <div className="sm:hidden -mt-2 mb-6 space-y-2" data-testid="dashboard-workflow">
          {upcoming.map((b) => {
            const m = TYPE_META[b.type];
            return (
              <button
                key={b.id}
                onClick={() => navigate(`/app/calendar?booking=${b.id}`)}
                className={`w-full flex items-center gap-3 rounded-lg border border-border border-l-4 ${m.bar} ${m.bg} px-3 py-2 text-left transition-all active:scale-[0.99]`}
                data-testid={`workflow-${b.id}`}
              >
                <span className="text-xs font-semibold text-foreground/70 whitespace-nowrap tabular-nums">{formatDateTime(b.start)}</span>
                <span className={`text-xs font-bold ${m.text} whitespace-nowrap`}>{m.label}</span>
                <span className="text-xs text-muted-foreground truncate flex-1">{b.house_name}</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground/50 shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {!stats ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {displayCards.map((c) => {
            const dest = c.to?.(user.role);
            const noticeCount = notices.length;
            const urgentCount = notices.filter((n) => n.priority === "urgent").length;
            const badge = c.key === "houses" && showNotices && !isOwner && noticeCount > 0 ? (
              <span
                className={`absolute top-3 right-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${urgentCount ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}
                title={urgentCount ? `${urgentCount} urgente prikbord-item(s)` : `${noticeCount} prikbord-item(s)`}
                data-testid="houses-notice-badge"
              >
                <Megaphone className="h-3 w-3" /> {noticeCount}
              </span>
            ) : c.key === "chat_unread" && (stats.chat_flame || 0) > 0 ? (
              <span
                className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold bg-orange-100 text-orange-700 animate-pulse"
                title={tx("{0} gesprek(ken) met ongelezen berichten ouder dan 30 min — snel reageren", { 0: stats.chat_flame })}
                data-testid="chat-flame-badge"
              >
                <Flame className="h-3 w-3" /> {stats.chat_flame}
              </span>
            ) : null;
            const inner = (
              <>
                {badge}
                <span className={`grid place-items-center h-11 w-11 rounded-lg ${c.color}`}><c.icon className="h-5 w-5" /></span>
                {c.key === "upcoming_bookings" && isTeam ? (
                  <div className={`mt-4 font-display font-bold text-3xl ${agendaAlerts > 0 ? "text-red-600" : ""}`} data-testid="agenda-attention-count">{agendaAlerts}</div>
                ) : c.key === "supplies" ? (
                  <div className="mt-4 font-display font-bold text-3xl" data-testid="stat-value-supplies">{supplies.length}</div>
                ) : c.key === "key_sets" ? (
                  <div className={`mt-4 font-display font-bold text-3xl ${stats.key_sets_assigned_red ? "text-red-600" : ""}`} data-testid="key-sets-assigned-count">{stats.key_sets_assigned ?? 0}</div>
                ) : (
                  <div className={`mt-4 font-display font-bold text-3xl ${ownerRed(c.key) ? "text-red-600" : ""}`} data-testid={`stat-value-${c.key}`}>{stats[c.key] ?? 0}</div>
                )}
                <div className="text-sm text-muted-foreground">{c.label}</div>
              </>
            );
            const onClick = c.key === "supplies" ? () => setSuppliesOpen(true) : dest ? () => navigate(dest) : null;
            return onClick ? (
              <button
                key={c.key}
                onClick={onClick}
                className="relative text-left bg-card rounded-xl border border-border p-6 shadow-sm hover:shadow-md hover:border-primary/40 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                data-testid={`stat-${c.key}`}
              >
                {inner}
              </button>
            ) : (
              <div key={c.key} className="relative bg-card rounded-xl border border-border p-6 shadow-sm" data-testid={`stat-${c.key}`}>
                {inner}
              </div>
            );
          })}
        </div>
      )}

      <SuppliesDialog open={suppliesOpen} onOpenChange={setSuppliesOpen} items={supplies} isOwner={isOwner}
        onDone={(x) => setSupplies((p) => p.filter((i) => supplyKey(i) !== supplyKey(x)))} />

      {showNotices && notices.length > 0 && (
        <div className="mt-8" data-testid="dashboard-attention">
          <div className="flex items-center gap-2 mb-3">
            <Megaphone className="h-5 w-5 text-primary" />
            <h2 className="font-display font-semibold text-lg">{["client", "host"].includes(user.role) ? tx("Prikbord van uw woningen") : tx("Aandachtspunten")}</h2>
            <span className="text-xs text-muted-foreground">({notices.length})</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {notices.map((n) => {
              const urgent = n.priority === "urgent";
              return (
                <button
                  key={n.id}
                  onClick={() => navigate(`/app/houses/${n.house_id}`)}
                  className={`text-left rounded-xl border p-4 transition-all hover:shadow-md ${urgent ? "border-red-300 bg-red-50" : "border-amber-200 bg-amber-50"}`}
                  data-testid={`attention-${n.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {urgent ? <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" /> : <Megaphone className="h-4 w-4 text-amber-600 shrink-0" />}
                    <span className={`font-display font-semibold text-sm ${urgent ? "text-red-800" : "text-amber-900"}`}>{n.house_name || tx("Woning")}</span>
                    {n.expires_at && <span className="ml-auto text-[11px] text-muted-foreground">{tx("tot")} {n.expires_at.slice(8, 10)}-{n.expires_at.slice(5, 7)}</span>}
                  </div>
                  <NoticeText n={n} className={`text-sm line-clamp-2 ${urgent ? "text-red-700" : "text-amber-800"}`} testId={`dash-notice-text-${n.id}`} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isHost ? (
        <button
          onClick={() => navigate("/app/linen")}
          className="mt-8 w-full text-left bg-gradient-to-t from-primary/15 via-background to-background rounded-xl border border-border p-6 shadow-sm hover:shadow-md hover:border-primary/40 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
          data-testid="dashboard-linen-card"
        >
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-lg text-secondary bg-secondary/10"><Shirt className="h-5 w-5" /></span>
            <h2 className="font-display font-semibold text-lg">{tx("Linnen")}</h2>
          </div>
          <p className="text-muted-foreground text-sm mt-2">{tx("Bekijk en registreer het linnen van uw woningen.")}</p>
        </button>
      ) : (
        <div className="mt-8 bg-card rounded-xl border border-border p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <img src={FAVICON} alt="" className="h-9 w-9 rounded-full shrink-0" />
            <h2 className="font-display font-semibold text-lg">{tx("Aan de slag")}</h2>
          </div>
          <p className="text-muted-foreground text-sm mt-2">
            {["admin"].includes(user.role)
              ? tx("Beheer uw woningen, plan sleuteloverdrachten en wijs taken toe aan uw team via het menu links.")
              : user.role === "client"
              ? tx("Bekijk alle belangrijke details van uw woning, het welkomstboek, de geplande afspraken en meer.")
              : user.role === "guest"
              ? tx("Bekijk het welkomstboek en de details van uw verblijf bij de woningen.")
              : tx("Bekijk uw toegewezen taken en afspraken via het menu links.")}
          </p>
        </div>
      )}
    </div>
  );
}
